<template>
  <div class="mt-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/40">
    <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
      <button
        type="button"
        :disabled="uploading"
        @click="triggerFileDialog"
        class="flex h-20 w-full items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white transition-colors hover:border-indigo-400 hover:bg-indigo-50/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:hover:border-indigo-500 dark:hover:bg-indigo-900/20 sm:h-20 sm:w-20"
      >
        <img
          v-if="displayPreviewUrl"
          :src="displayPreviewUrl"
          alt="Favicon 预览"
          class="h-10 w-10 object-contain"
        />
        <span v-else class="px-2 text-center text-[11px] leading-4 text-gray-400 dark:text-gray-500">暂无图标</span>
      </button>

      <div class="min-w-0 flex-1">
        <p class="text-sm font-medium text-gray-900 dark:text-gray-100">Favicon 上传</p>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          先选择图片，再在弹窗中裁剪、平移、旋转，并统一导出为 128x128 PNG。
        </p>
        <div class="mt-3 flex flex-wrap items-center gap-2">
          <input
            ref="fileInputRef"
            type="file"
            class="hidden"
            accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
            @change="handleFileChange"
          />

          <button
            type="button"
            :disabled="uploading"
            @click="triggerFileDialog"
            class="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            {{ uploading ? '上传中...' : currentUrl ? '重新选择' : '选择图片' }}
          </button>

          <span class="text-xs text-gray-500 dark:text-gray-400">源图至少 64x64；最终导出 128x128 PNG，最大 256KB。</span>
        </div>
      </div>
    </div>

    <TenantFaviconCropModal
      :show="showCropModal"
      :source-image-url="selectedSourceUrl"
      :original-file-name="selectedFileName"
      :confirming="uploading"
      @close="closeCropModal"
      @confirm="handleModalConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import TenantFaviconCropModal from '@/components/TenantFaviconCropModal.vue'
import { uploadTenantFaviconAPI } from '@/api/admin/tenants'
import toaster from '@/utils/toaster'
import type { TenantFaviconUploadResponse } from '@/types/admin/tenant'

const MIN_SOURCE_SIZE = 64
const MAX_OUTPUT_SIZE = 256 * 1024

interface Props {
  tenantId: number
  currentUrl: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  uploaded: [payload: TenantFaviconUploadResponse]
}>()

const fileInputRef = ref<HTMLInputElement | null>(null)
const uploading = ref(false)
const showCropModal = ref(false)
const selectedSourceUrl = ref('')
const selectedFileName = ref('')
const previewOverrideUrl = ref('')
const lastUploadedFaviconUrl = ref('')

/**
 * 当前预览地址。
 */
const displayPreviewUrl = computed(() => previewOverrideUrl.value || props.currentUrl)

/**
 * 释放当前源文件预览地址。
 */
const revokeSourceUrl = () => {
  if (selectedSourceUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(selectedSourceUrl.value)
  }
  selectedSourceUrl.value = ''
}

/**
 * 重置当前待编辑文件状态。
 */
const resetSelectedFile = () => {
  revokeSourceUrl()
  selectedFileName.value = ''
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

/**
 * 关闭裁剪弹窗。
 */
const closeCropModal = () => {
  if (uploading.value) {
    return
  }
  showCropModal.value = false
  resetSelectedFile()
}

/**
 * 打开文件选择器。
 */
const triggerFileDialog = () => {
  if (uploading.value) {
    return
  }
  fileInputRef.value?.click()
}

/**
 * 校验待裁剪文件的类型。
 */
const validateFileType = (file: File) => {
  const allowedTypes = ['image/png', 'image/jpeg', 'image/webp']
  const allowedExtensions = ['png', 'jpg', 'jpeg', 'webp']
  const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() || '' : ''

  if ((file.type && !allowedTypes.includes(file.type)) || !allowedExtensions.includes(extension)) {
    throw new Error('仅支持 PNG、JPG、JPEG、WEBP 格式的图片')
  }
}

/**
 * 读取图片宽高。
 */
const loadImageDimensions = async (sourceUrl: string): Promise<{ width: number; height: number }> => {
  return await new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
      })
    }
    image.onerror = () => reject(new Error('无法读取图片尺寸'))
    image.src = sourceUrl
  })
}

/**
 * 校验源图尺寸。
 */
const validateSourceDimensions = async (sourceUrl: string) => {
  const { width, height } = await loadImageDimensions(sourceUrl)
  if (width < MIN_SOURCE_SIZE || height < MIN_SOURCE_SIZE) {
    throw new Error(`原图尺寸不能小于 ${MIN_SOURCE_SIZE}x${MIN_SOURCE_SIZE}`)
  }
}

/**
 * 选择原图后打开裁剪弹窗。
 */
const handleFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) {
    return
  }

  const nextSourceUrl = URL.createObjectURL(file)

  try {
    validateFileType(file)
    await validateSourceDimensions(nextSourceUrl)
  } catch (error) {
    URL.revokeObjectURL(nextSourceUrl)
    resetSelectedFile()
    toaster.error((error as Error).message)
    return
  }

  resetSelectedFile()
  selectedFileName.value = file.name
  selectedSourceUrl.value = nextSourceUrl
  showCropModal.value = true
}

/**
 * 确认裁剪后上传最终文件。
 */
const handleModalConfirm = async (file: File) => {
  if (file.size > MAX_OUTPUT_SIZE) {
    toaster.error('Favicon 文件大小不能超过 256KB，请更换更简洁的图片后重试')
    return
  }

  uploading.value = true

  try {
    const response = await uploadTenantFaviconAPI(props.tenantId, file)
    if (response.status !== 200 || !response.data) {
      throw new Error(response.message || '上传 Favicon 失败')
    }

    toaster.success('上传 Favicon 成功')
    previewOverrideUrl.value = response.data.previewUrl || ''
    lastUploadedFaviconUrl.value = response.data.faviconUrl
    emit('uploaded', {
      faviconUrl: response.data.faviconUrl,
      previewUrl: response.data.previewUrl || '',
    })
    showCropModal.value = false
    resetSelectedFile()
  } catch (error) {
    toaster.error((error as Error).message || '上传 Favicon 失败')
  } finally {
    uploading.value = false
  }
}

watch(
  () => props.currentUrl,
  (nextUrl) => {
    if (!nextUrl) {
      previewOverrideUrl.value = ''
      lastUploadedFaviconUrl.value = ''
      return
    }

    if (lastUploadedFaviconUrl.value && nextUrl !== lastUploadedFaviconUrl.value) {
      previewOverrideUrl.value = ''
      lastUploadedFaviconUrl.value = ''
    }
  },
)

onBeforeUnmount(() => {
  resetSelectedFile()
})
</script>
